import React, { createContext, useCallback, useContext, useState } from 'react';
import type { ReactNode } from 'react';

import { STORE_CART_UI_VARIATION_VALUES, type StoreCartUiVariation } from '../storeCart.constants';

interface IStoreCartUiVariationContext {
  uiVariation: StoreCartUiVariation;
  setUiVariation: (variation: StoreCartUiVariation) => void;
}

type StoreCartUiVariationDispatch = ((variation: StoreCartUiVariation) => void) | null;

const StoreCartUiVariationStateContext = createContext<StoreCartUiVariation | null>(null);
const StoreCartUiVariationDispatchContext = createContext<StoreCartUiVariationDispatch>(null);

export const StoreCartUiVariationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [uiVariation, setUiVariationState] = useState<StoreCartUiVariation>(
    STORE_CART_UI_VARIATION_VALUES.PHASE_2
  );

  const setUiVariation = useCallback((variation: StoreCartUiVariation) => {
    setUiVariationState((current) => (current === variation ? current : variation));
  }, []);

  return (
    <StoreCartUiVariationStateContext.Provider value={uiVariation}>
      <StoreCartUiVariationDispatchContext.Provider value={setUiVariation}>
        {children}
      </StoreCartUiVariationDispatchContext.Provider>
    </StoreCartUiVariationStateContext.Provider>
  );
};

export const useStoreCartUiVariationDispatch = (): ((variation: StoreCartUiVariation) => void) => {
  const setUiVariation = useContext(StoreCartUiVariationDispatchContext);
  if (!setUiVariation) {
    throw new Error(
      'useStoreCartUiVariationDispatch must be used within StoreCartUiVariationProvider'
    );
  }
  return setUiVariation;
};

export const useStoreCartUiVariationStateOptional = (): StoreCartUiVariation | null =>
  useContext(StoreCartUiVariationStateContext);

export const useStoreCartUiVariation = (): IStoreCartUiVariationContext => {
  const uiVariation = useContext(StoreCartUiVariationStateContext);
  const setUiVariation = useContext(StoreCartUiVariationDispatchContext);
  if (!uiVariation || !setUiVariation) {
    throw new Error('useStoreCartUiVariation must be used within StoreCartUiVariationProvider');
  }
  return { uiVariation, setUiVariation };
};

export const useStoreCartUiVariationOptional = (): IStoreCartUiVariationContext | null => {
  const uiVariation = useContext(StoreCartUiVariationStateContext);
  const setUiVariation = useContext(StoreCartUiVariationDispatchContext);
  if (!uiVariation || !setUiVariation) {
    return null;
  }
  return { uiVariation, setUiVariation };
};

StoreCartUiVariationProvider.displayName = 'StoreCartUiVariationProvider';
